import './index.scss';
import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Loader } from '@piggytechng/component-library';
import Header from '../../../../../../../components/Header/Header';
import NoData from '../../../../../../../components/NoData/NoData';
import Pagination from '../../../../../../../components/Pagination/Pagination';
import { fetchTargetList } from '../../../../../../../hooks/useTargetSavings';
import { formatToNaira } from '../../../../../../../helpers/utils';
import { formatTargetDate } from '../../../../../../../helpers/utils/targetSavings';
import { TargetListItem, TargetSavingsTab } from '../../../../../../../types';

const SEARCH_DEBOUNCE_MS = 400;
const DEFAULT_LIMIT = 20;

const TABS: Array<{ key: TargetSavingsTab; label: string }> = [
  { key: 'active', label: 'Active' },
  { key: 'completed', label: 'Completed' },
  { key: 'all', label: 'All' },
];

const isKnownTab = (value: string | undefined): value is TargetSavingsTab =>
  value === 'active' || value === 'completed' || value === 'all';

const statusBadgeModifier = (status?: string) => {
  const key = (status ?? '').toLowerCase();
  if (key === 'active') return 'active';
  if (key === 'completed') return 'completed';
  return 'unknown';
};

const formatMoney = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') return '-';
  return formatToNaira(value);
};

const index = () => {
  const params = useParams();
  const id = params.id as string;
  const activeTab: TargetSavingsTab = isKnownTab(params.activeTab) ? params.activeTab : 'all';

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = (searchParams.get('q') ?? '').trim();

  const [searchInput, setSearchInput] = useState(q);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applySearch = (value: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const next = value.trim();
    if (next === q) return;
    setPage(1);
    setSearchParams(next ? { q: next } : {}, { replace: true });
  };

  // Debounced search: typing updates ?q= after a pause; Enter / Search submit immediately.
  useEffect(() => {
    if (searchInput.trim() === q) return;
    debounceRef.current = setTimeout(() => applySearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // Keep the input in sync when ?q= changes from outside (back button, wallet-tab handoff).
  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    applySearch(searchInput);
  };

  const handleReset = () => {
    setSearchInput('');
    applySearch('');
  };

  const switchTab = (tab: TargetSavingsTab) => {
    if (tab === activeTab) return;
    setPage(1);
    navigate(`/view-all-targets/${id}/${tab}${q ? `?q=${encodeURIComponent(q)}` : ''}`, { replace: true });
  };

  const {
    data: targetList,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['target-list', id, activeTab, q, page, limit],
    enabled: !!id,
    retry: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    queryFn: () => fetchTargetList(id, { status: activeTab, q, page, limit }),
  });

  const rows: TargetListItem[] = targetList?.results ?? [];
  const pageTitle = activeTab === 'all' ? 'All Targets' : `${TABS.find((t) => t.key === activeTab)?.label} Targets`;

  return (
    <div className="view-all-target">
      <Header name={pageTitle} backButton />

      <div className="content">
        <div className="wallets-details target-list-tabs">
          <div className="history-tab-switch target">
            {TABS.map((tab) => (
              <span
                key={tab.key}
                className={`history-pill${activeTab === tab.key ? ' active' : ''}`}
                onClick={() => switchTab(tab.key)}
              >
                {tab.label}
              </span>
            ))}
          </div>
        </div>

        <div className="customers-filter">
          <div className="input-field">
            <form action="" onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder="Search targets by title"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Search targets by title"
              />
            </form>
          </div>
          <div className="query-filter" onClick={() => applySearch(searchInput)}>
            <div>
              <svg width="10" height="10" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M0.75 0.75H17.25M6.75 13.25H11.25M3.75 7H14.25"
                  stroke="#1E293B"
                  strokeWidth="1.5"
                  strokeLinecap="square"
                />
              </svg>
            </div>
            <div>Search</div>
          </div>
          <div className="query-filter" onClick={handleReset}>
            <div></div>
            <div>Reset</div>
          </div>
        </div>

        {targetList?.fallback && (
          <p className="target-list-note">Search is limited to this page until the backend update ships</p>
        )}

        {isLoading ? (
          <div className="page-loader">
            <Loader color="#0F60D6" />
          </div>
        ) : isError ? (
          <NoData message={'Could not load targets. Please try again.'} />
        ) : rows.length ? (
          <div className={`table-container target-list-table${isFetching ? ' target-list-table--refreshing' : ''}`}>
            <table className="table">
              <thead className="table_head">
                <tr>
                  <th className="title">Title</th>
                  <th className="status">Status</th>
                  <th className="role">Role</th>
                  <th className="amount">Balance</th>
                  <th className="amount">Target amount</th>
                  <th className="date">Start</th>
                  <th className="date">End</th>
                  <th className="date">Created</th>
                </tr>
              </thead>

              <tbody className="table-body">
                {rows.map((item, i) => (
                  <tr
                    key={item.targetId || i}
                    className="table-row"
                    onClick={() => navigate(`/view-target/${id}/${item.targetId}`)}
                  >
                    <td className="title" title={item.title}>
                      {item.title || '-'}
                    </td>
                    <td className="status">
                      <span
                        className={`customer-status-badge customer-status-badge--${statusBadgeModifier(item.status)}`}
                      >
                        <span className="customer-status-badge__dot" />
                        {item.status || 'unknown'}
                      </span>
                      {item.archived && (
                        <span className="customer-status-badge customer-status-badge--removed" title="Archived">
                          <span className="customer-status-badge__dot" />
                          Archived
                        </span>
                      )}
                    </td>
                    <td className="role">{item.role || '-'}</td>
                    <td className="amount">{formatMoney(item.userBalance)}</td>
                    <td className="amount">{formatMoney(item.targetAmount)}</td>
                    <td className="date">{formatTargetDate(item.startDate)}</td>
                    <td className="date">{formatTargetDate(item.endDate)}</td>
                    <td className="date">{formatTargetDate(item.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <NoData message={q ? `No targets match "${q}".` : 'No targets at the moment.'} />
        )}

        {!isLoading && !isError && (
          <Pagination
            currentPage={page}
            totalPages={targetList?.totalPages ?? 1}
            onPageChange={setPage}
            limit={limit}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default index;
